import { useEffect, useState } from "react";
import Card from "../components/Card";
import Modal from "../components/Modal";
import AddCardModal from "../payment/AddCardModal";
import { useRequest } from "../hooks/useRequest";
import { getProfile, updatePassword } from "../api/user";
import {
  getPaymentMethods,
  deletePaymentMethod,
  setDefaultPaymentMethod,
  PaymentMethod,
} from "../api/paymentMethods";
import "./AccountSettings.scss";

/** 卡品牌首字母大写；expMonth/expYear → "MM/YY" */
function brandLabel(brand: string): string {
  return brand.charAt(0).toUpperCase() + brand.slice(1);
}
function expiry(m: number, y: number): string {
  return `${String(m).padStart(2, "0")}/${String(y % 100).padStart(2, "0")}`;
}

const AccountSettings = () => {
  useEffect(() => {
    document.title = "Account Settings | CloudStore API";
  }, []);

  const profile = useRequest(getProfile);
  const cards = useRequest(getPaymentMethods);

  // 卡片操作状态
  const [busyCardId, setBusyCardId] = useState<string | null>(null);
  const [defaultErrId, setDefaultErrId] = useState<string | null>(null);
  const [defaultErr, setDefaultErr] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<PaymentMethod | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeErr, setRemoveErr] = useState<string | null>(null);
  const [addCardOpen, setAddCardOpen] = useState(false);

  // 密码表单状态
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [pwdSubmitting, setPwdSubmitting] = useState(false);
  const [pwdMsg, setPwdMsg] = useState<string | null>(null);
  const [pwdErr, setPwdErr] = useState<string | null>(null);

  const handleSetDefault = async (id: string) => {
    setDefaultErrId(null);
    setDefaultErr(null);
    setBusyCardId(id);
    try {
      await setDefaultPaymentMethod(id);
      cards.reload();
    } catch (e) {
      setDefaultErrId(id);
      setDefaultErr(
        e instanceof Error ? e.message : "Failed to set default payment method."
      );
    } finally {
      setBusyCardId(null);
    }
  };

  const confirmRemove = async () => {
    if (!removeTarget) return;
    setRemoveErr(null);
    setRemoving(true);
    try {
      await deletePaymentMethod(removeTarget.id);
      setRemoveTarget(null);
      cards.reload();
    } catch (e) {
      setRemoveErr(
        e instanceof Error ? e.message : "Failed to remove payment method."
      );
    } finally {
      setRemoving(false);
    }
  };

  const handleUpdatePassword = async () => {
    setPwdMsg(null);
    setPwdErr(null);
    if (!currentPassword) return setPwdErr("Please enter your current password.");
    if (!newPassword) return setPwdErr("Please enter a new password.");
    if (newPassword !== confirmNewPassword)
      return setPwdErr("Passwords do not match.");
    setPwdSubmitting(true);
    try {
      const res = await updatePassword(currentPassword, newPassword);
      setPwdMsg(res.message);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
    } catch (e) {
      setPwdErr(e instanceof Error ? e.message : "Failed to update password.");
    } finally {
      setPwdSubmitting(false);
    }
  };

  const cardList = cards.data || [];

  return (
    <div className="cs-account">
      <h1 className="cs-account__title">Account Settings</h1>

      {/* 账户信息 */}
      <Card>
        <h2 className="cs-account__section-title">Account</h2>
        <div className="cs-account__label">Account Email</div>
        <div className="cs-account__readonly">
          {profile.loading ? "Loading…" : profile.data?.email || "—"}
        </div>
      </Card>

      {/* 支付方式 */}
      <Card>
        <h2 className="cs-account__section-title">Payment Methods</h2>

        {cards.loading && (
          <div className="cs-cards__empty">Loading…</div>
        )}
        {!cards.loading && cards.error && (
          <div className="cs-account__err">{cards.error}</div>
        )}

        {!cards.loading && !cards.error && (
          <div className="cs-cards">
            {cardList.length === 0 ? (
              <div className="cs-cards__empty">No payment methods yet.</div>
            ) : (
              cardList.map((c) => (
                <div key={c.id} className="cs-card-row">
                <div
                  className={`cs-card-item${
                    c.isDefault ? " cs-card-item--default" : ""
                  }`}
                >
                  <div className="cs-card-item__info">
                    <span className="cs-card-item__brand">💳</span>
                    <div className="cs-card-item__main">
                      <div className="cs-card-item__number">
                        {brandLabel(c.brand)} •••• {c.last4}
                      </div>
                      <div className="cs-card-item__meta">
                        Expires {expiry(c.expMonth, c.expYear)}
                      </div>
                    </div>
                  </div>

                  <div className="cs-card-item__right">
                    {c.isDefault ? (
                      <span className="cs-card-item__badge">Default</span>
                    ) : (
                      <button
                        type="button"
                        className="cs-card-item__action"
                        disabled={busyCardId === c.id}
                        onClick={() => handleSetDefault(c.id)}
                      >
                        {busyCardId === c.id ? "Setting…" : "Set as default"}
                      </button>
                    )}
                    <button
                      type="button"
                      className="cs-card-item__action cs-card-item__action--danger"
                      onClick={() => {
                        setRemoveErr(null);
                        setRemoveTarget(c);
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
                {defaultErrId === c.id && defaultErr && (
                  <p className="cs-card-item__err">{defaultErr}</p>
                )}
                </div>
              ))
            )}
          </div>
        )}

        <button
          type="button"
          className="cs-account__add-card"
          onClick={() => setAddCardOpen(true)}
        >
          + Add New Card
        </button>
      </Card>

      {/* 安全 - 修改密码 */}
      <Card>
        <h2 className="cs-account__section-title">Security</h2>
        <div className="cs-account__field">
          <div className="cs-account__label">Current Password</div>
          <input
            className="cs-account__input"
            type="password"
            placeholder="Current Password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
        </div>
        <div className="cs-account__field">
          <div className="cs-account__label">New Password</div>
          <input
            className="cs-account__input"
            type="password"
            placeholder="New Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </div>
        <div className="cs-account__field">
          <div className="cs-account__label">Confirm New Password</div>
          <input
            className="cs-account__input"
            type="password"
            placeholder="Confirm New Password"
            value={confirmNewPassword}
            onChange={(e) => setConfirmNewPassword(e.target.value)}
          />
        </div>

        {pwdErr && <p className="cs-account__err">{pwdErr}</p>}
        {pwdMsg && !pwdErr && <p className="cs-account__msg">{pwdMsg}</p>}

        <button
          type="button"
          className="cs-account__update-btn"
          disabled={pwdSubmitting}
          onClick={handleUpdatePassword}
        >
          {pwdSubmitting ? "Updating…" : "Update Password"}
        </button>
      </Card>

      {/* 添加信用卡弹窗（Stripe 绑卡，流程一） */}
      <AddCardModal
        open={addCardOpen}
        onClose={() => setAddCardOpen(false)}
        onAdded={() => cards.reload()}
      />

      {/* 删除卡确认弹窗 */}
      <Modal
        open={!!removeTarget}
        onClose={() => {
          setRemoveTarget(null);
          setRemoveErr(null);
        }}
      >
        <div className="cs-remove">
          <div className="cs-remove__header">
            <h2 className="cs-remove__title">Remove Payment Method</h2>
            <button
              type="button"
              className="cs-remove__close"
              aria-label="Close"
              onClick={() => {
                setRemoveTarget(null);
                setRemoveErr(null);
              }}
            >
              ✕
            </button>
          </div>

          <p className="cs-remove__text">
            Are you sure you want to remove{" "}
            {removeTarget ? brandLabel(removeTarget.brand) : ""} ****{" "}
            {removeTarget?.last4}?
          </p>

          <div className="cs-remove__warn">This action cannot be undone.</div>

          {removeErr && <p className="cs-remove__err">{removeErr}</p>}

          <div className="cs-remove__actions">
            <button
              type="button"
              className="cs-remove__cancel"
              onClick={() => {
                setRemoveTarget(null);
                setRemoveErr(null);
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="cs-remove__confirm"
              disabled={removing}
              onClick={confirmRemove}
            >
              {removing ? "Removing…" : "Remove Card"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AccountSettings;
